import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';

const PRIVACY_URL = 'https://www.tippay.co.in/privacy';
const TERMS_URL = 'https://www.tippay.co.in/terms';
function readStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function formatTipModeLabel(mode) {
  const m = (mode || '').toLowerCase();
  if (m === 'individual') return 'Individual';
  if (m === 'pool') return 'Pool';
  return 'Not set';
}

function IconCircle({ children, variant = 'primary' }) {
  return <span className={`more-icon-circle more-icon-circle--${variant}`}>{children}</span>;
}

function ChevronRight() {
  return (
    <svg className="more-settings-chevron" width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M9 6l6 6-6 6V6z" />
    </svg>
  );
}

function SettingsRowLink({ to, icon, title, subtitle }) {
  return (
    <Link to={to} className="more-settings-row">
      {icon}
      <span className="more-settings-row-text">
        <span className="more-settings-row-title">{title}</span>
        {subtitle ? <span className="more-settings-row-sub">{subtitle}</span> : null}
      </span>
      <ChevronRight />
    </Link>
  );
}

function SettingsRowAnchor({ href, external, icon, title, subtitle }) {
  return (
    <a
      href={href}
      className="more-settings-row"
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    >
      {icon}
      <span className="more-settings-row-text">
        <span className="more-settings-row-title">{title}</span>
        {subtitle ? <span className="more-settings-row-sub">{subtitle}</span> : null}
      </span>
      <ChevronRight />
    </a>
  );
}

function ShopIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
      />
    </svg>
  );
}

function BankIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M4 10v7h3v-7H4zm6 0v7h3v-7h-3zM2 22h19v-3H2v3zm14-12v7h3v-7h-3zm-4.5-9L2 6v2h20V6l-9.5-5z"
      />
    </svg>
  );
}

function TipTypeIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
      />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
      />
    </svg>
  );
}

function HeadsetIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 1c-4.97 0-9 4.03-9 9v7c0 1.66 1.34 3 3 3h3v-8H5v-2c0-3.87 3.13-7 7-7s7 3.13 7 7v2h-4v8h3c1.66 0 3-1.34 3-3v-7c0-4.97-4.03-9-9-9z"
      />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"
      />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"
      />
    </svg>
  );
}

function ProfileAvatar({ src, name }) {
  const [broken, setBroken] = useState(false);
  const initial = (name && name.trim()[0]) || '?';
  if (src && !broken) {
    return (
      <img
        className="more-profile-avatar"
        src={src}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span className="more-profile-avatar more-profile-avatar--placeholder" aria-hidden="true">
      {initial.toUpperCase()}
    </span>
  );
}

function MorePage({ onLogout }) {
  const stored = readStoredUser();
  const [shopInfo, setShopInfo] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [logoutOpen, setLogoutOpen] = useState(false);

  useEffect(() => {
    if (!logoutOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLogoutOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [logoutOpen]);

  const loadProfile = useCallback(async (signal) => {
    try {
      const data = await webApi.getShopInfo({ signal });
      if (signal.aborted) return;
      setShopInfo(data);
      setLoadError('');
    } catch (err) {
      if (signal.aborted || isCanceledRequest(err)) return;
      setLoadError(err.message || 'Failed to load profile');
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    loadProfile(ac.signal);
    return () => ac.abort();
  }, [loadProfile]);

  const displayName = shopInfo?.name || stored?.name || 'Employer';
  const employerCode = shopInfo?.employerCode || stored?.empCode || '—';
  const tipModeLabel = formatTipModeLabel(shopInfo?.defaultTipMode);
  const profilePictureUrl = shopInfo?.profilePictureUrl || null;

  const confirmLogout = () => {
    setLogoutOpen(false);
    onLogout();
  };

  return (
    <div className="more-page">
      <header className="more-header">
        <h1 className="more-header-title">Profile &amp; Settings</h1>
        <div className="more-profile-card">
          <ProfileAvatar src={profilePictureUrl} name={displayName} />
          <div className="more-profile-text">
            <p className="more-profile-name">{displayName}</p>
            <p className="more-profile-code">{employerCode}</p>
          </div>
        </div>
      </header>

      {loadError ? <p className="more-inline-error">{loadError}</p> : null}

      <section className="more-settings-section" aria-label="Settings">
        <h2 className="more-settings-heading">Settings</h2>
        <div className="more-settings-list">
          <SettingsRowLink
            to="/shop-info"
            title="Shop Information"
            icon={
              <IconCircle>
                <ShopIcon />
              </IconCircle>
            }
          />
          <div className="more-settings-divider" role="presentation" />
          <SettingsRowLink
            to="/bank-accounts"
            title="Bank Details"
            icon={
              <IconCircle>
                <BankIcon />
              </IconCircle>
            }
          />
          <div className="more-settings-divider" role="presentation" />
          <SettingsRowLink
            to="/tip-mode"
            title="Change Tip Type"
            subtitle={`Current: ${tipModeLabel}`}
            icon={
              <IconCircle>
                <TipTypeIcon />
              </IconCircle>
            }
          />
          <div className="more-settings-divider" role="presentation" />
          <SettingsRowLink
            to="/change-password"
            title="Change Password"
            icon={
              <IconCircle>
                <LockIcon />
              </IconCircle>
            }
          />
          <div className="more-settings-divider" role="presentation" />
          <SettingsRowLink
            to="/contact-us"
            title="Contact Us"
            icon={
              <IconCircle>
                <HeadsetIcon />
              </IconCircle>
            }
          />
          <div className="more-settings-divider" role="presentation" />
          <SettingsRowAnchor
            href={PRIVACY_URL}
            external
            icon={
              <IconCircle>
                <DocIcon />
              </IconCircle>
            }
            title="Privacy Policy"
          />
          <div className="more-settings-divider" role="presentation" />
          <SettingsRowAnchor
            href={TERMS_URL}
            external
            icon={
              <IconCircle>
                <DocIcon />
              </IconCircle>
            }
            title="Terms &amp; Conditions"
          />
          <div className="more-settings-divider" role="presentation" />
          <button type="button" className="more-settings-row more-settings-row--logout" onClick={() => setLogoutOpen(true)}>
            <IconCircle variant="danger">
              <LogoutIcon />
            </IconCircle>
            <span className="more-settings-row-text">
              <span className="more-settings-row-title">Log Out</span>
            </span>
          </button>
        </div>
      </section>

      {logoutOpen ? (
        <div
          className="more-dialog-overlay"
          role="presentation"
          onClick={() => setLogoutOpen(false)}
        >
          <div
            className="more-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="more-logout-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="more-logout-title" className="more-dialog-title">
              Log Out
            </h2>
            <p className="more-dialog-body">Are you sure you want to log out?</p>
            <div className="more-dialog-actions">
              <button type="button" className="more-dialog-action" onClick={() => setLogoutOpen(false)}>
                Cancel
              </button>
              <button type="button" className="more-dialog-action" onClick={confirmLogout}>
                Log Out
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default MorePage;
